import { viewDocument } from "../../../api/elearning/documents/documents";
import { getDocumentAccessMe } from "../../../api/elearning/documents/documentAccess";
import { showConfirmDialog, showErrorToast, showSuccessToast } from "../../../components/ConfirmDialog";
import { MAX_ZIP_BYTES_FOR_INLINE_PLAYER } from "../../../utils/ispringZipPresentation";
import { openCachedLessonZipViaServiceWorker } from "../../../utils/imaLessonSwPresentation";
import {
  getLessonBaiGiangZipFromDevice,
} from "../../../utils/lessonZipDeviceCache";
import {
  putEncryptedLessonDocument,
  getDecryptedLessonDocumentIfMatch,
  hasSecureBaiGiangCache,
} from "../../../utils/secureLessonDocumentCache";

export function isBaiGiangType(t) {
  return t === "baigiang" || t === "bai_giang";
}

export function isPdfDoc(d) {
  const ext = (d?.file_extension || "").toLowerCase();
  return ext === "pdf" || String(d?.file_type || "").toLowerCase().includes("pdf");
}

export function baiGiangZipRevision(d) {
  if (d?.last_changed != null && String(d.last_changed).length > 0) return String(d.last_changed);
  if (d?.file_size != null) return `sz:${d.file_size}`;
  return String(d?.id || "");
}

/**
 * Đã có bản ZIP bài giảng trên máy khớp phiên bản hiện tại (metadata IndexedDB hoặc cache legacy).
 * Không yêu cầu khóa trong sessionStorage: khi trình chiếu, `getDecryptedLessonDocumentIfMatch` sẽ
 * gọi API lấy khóa nếu phiên mới chưa có — nút xanh chỉ báo «đã lưu cục bộ», tránh sai sau đăng nhập lại.
 */
export async function isBaiGiangZipReadyLocally(doc) {
  if (!doc?.id || !isBaiGiangType(doc.type)) return false;
  const rev = baiGiangZipRevision(doc);
  if (await hasSecureBaiGiangCache(doc.id, rev)) {
    return true;
  }
  try {
    const legacy = await getLessonBaiGiangZipFromDevice(doc.id);
    return Boolean(legacy?.buffer && String(legacy.revision) === String(rev));
  } catch {
    return false;
  }
}

export async function ensureDocumentAccessRemaining() {
  try {
    const a = await getDocumentAccessMe();
    if (!a.exempt && a.remaining <= 0) {
      showErrorToast(
        a.message ||
          "Bạn đã hết lượt tải/xem tài liệu trong tháng. Liên hệ quản trị để được cấp thêm lượt."
      );
      return false;
    }
    return true;
  } catch {
    return true;
  }
}

async function parseViewErrorMessage(err) {
  let msg = err?.response?.data?.message || err?.message || "Không tải được tài liệu.";
  const data = err?.response?.data;
  if (data instanceof Blob) {
    try {
      const t = await data.text();
      const j = JSON.parse(t);
      if (j?.message) msg = j.message;
    } catch {
      /* ignore */
    }
  }
  return msg;
}

/**
 * @param {object} doc
 * @param {{ onProgress?: (arg: { loaded: number; total: number }) => void }} [options]
 * @returns {Promise<boolean>}
 */
export async function downloadLessonDocument(doc, options = {}) {
  const { onProgress } = options;
  if (!(await ensureDocumentAccessRemaining())) return false;
  try {
    const res = await viewDocument(doc.id, {
      action: "download",
      onDownloadProgress: (evt) => {
        const loaded = evt?.loaded ?? 0;
        const total = evt?.total ?? 0;
        onProgress?.({ loaded, total });
      },
    });
    const buf = await res.data.arrayBuffer();
    const known = Number(doc?.file_size);
    if (Number.isFinite(known) && known > 0) {
      onProgress?.({ loaded: known, total: known });
    } else {
      onProgress?.({ loaded: 1, total: 1 });
    }
    const rev = baiGiangZipRevision(doc);
    await putEncryptedLessonDocument(doc, buf, rev);
    showSuccessToast(
      "Đã lưu vào bộ nhớ trong ứng dụng (không lưu vào thư mục Tải xuống)."
    );
    return true;
  } catch (err) {
    showErrorToast(await parseViewErrorMessage(err));
    return false;
  }
}

export async function presentLessonDocument(doc, options = {}) {
  const { setBusyId, onOpenPdfPreview, localReady = false } = options;
  const setBusy = (id) => setBusyId?.(id ?? null);

  if (isPdfDoc(doc)) {
    if (typeof onOpenPdfPreview === "function") {
      if (!(await ensureDocumentAccessRemaining())) return;
      onOpenPdfPreview(doc);
      return;
    }
    showErrorToast(
      "Tài liệu PDF chỉ xem trong ứng dụng — dùng nút «Xem PDF» / «Xem tài liệu» trên danh sách bài học."
    );
    return;
  }

  if (!isBaiGiangType(doc.type)) {
    showErrorToast("Trình chiếu nhanh chỉ hỗ trợ bài giảng (ZIP).");
    return;
  }

  const openPresentationWindow = () => {
    // Không dùng noopener: cần giữ tham chiếu để location.replace sau khi SW sẵn sàng.
    const win = window.open("about:blank", "_blank");
    if (!win) {
      showErrorToast(
        "Trình duyệt đã chặn cửa sổ mới. Hãy cho phép popup cho trang này (biểu tượng trên thanh địa chỉ) rồi bấm «Trình chiếu» lại."
      );
      return null;
    }
    try {
      win.document.write(
        `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Đang mở bài giảng…</title></head>` +
          `<body style="margin:0;min-height:100vh;display:flex;align-items:center;justify-content:center;` +
          `font-family:system-ui,sans-serif;background:#fff8f6;color:#444">` +
          `<p style="font-size:14px;font-weight:600">Đang mở bài giảng…</p></body></html>`
      );
      win.document.close();
    } catch {
      /* ignore cross-origin / closed */
    }
    return win;
  };

  let presentationWin = null;

  /**
   * Chỉ mở tab mới ngay khi đã biết ZIP sẵn trên máy — tránh tab trống + dialog
   * khi chưa tải (UX kém).
   */
  if (localReady) {
    presentationWin = openPresentationWindow();
    if (!presentationWin) return;
  }

  try {
    const rev = baiGiangZipRevision(doc);
    const secureBuf = await getDecryptedLessonDocumentIfMatch(doc.id, rev);
    if (secureBuf) {
      if (!presentationWin) {
        presentationWin = openPresentationWindow();
      }
      await openCachedLessonZipViaServiceWorker(secureBuf, {
        fallbackToBlob: true,
        preOpenedWindow: presentationWin || undefined,
      });
      return;
    }

    const local = await getLessonBaiGiangZipFromDevice(doc.id);
    if (local?.buffer && local.revision === rev) {
      if (!presentationWin) {
        presentationWin = openPresentationWindow();
      }
      await openCachedLessonZipViaServiceWorker(local.buffer, {
        fallbackToBlob: true,
        preOpenedWindow: presentationWin || undefined,
      });
      return;
    }

    // Chưa có file local: đóng tab nếu đã mở nhầm, hỏi xác nhận trên trang hiện tại.
    if (presentationWin && !presentationWin.closed) {
      presentationWin.close();
      presentationWin = null;
    }

    setBusy(doc.id);

    if (!(await ensureDocumentAccessRemaining())) {
      setBusy(null);
      return;
    }

    const confirm = await showConfirmDialog({
      title: "Chưa có bài giảng trên thiết bị",
      text:
        "Trên trình duyệt chưa lưu bản ZIP của bài giảng này (hoặc đã có bản mới trên máy chủ). Tải từ máy chủ sẽ dùng 1 lượt trong tháng. Bạn có muốn tải về thiết bị và trình chiếu?",
      confirmText: "Tải và trình chiếu",
      cancelText: "Hủy",
    });
    if (!confirm.isConfirmed) {
      setBusy(null);
      return;
    }

    // Mở tab ngay sau khi user bấm xác nhận (còn gesture), trước khi tải.
    presentationWin = openPresentationWindow();

    const res = await viewDocument(doc.id, { action: "download" });
    const buf = await res.data.arrayBuffer();
    await putEncryptedLessonDocument(doc, buf, rev);
    await openCachedLessonZipViaServiceWorker(buf, {
      fallbackToBlob: true,
      preOpenedWindow: presentationWin || undefined,
    });
  } catch (err) {
    if (presentationWin && !presentationWin.closed) {
      presentationWin.close();
    }
    showErrorToast((await parseViewErrorMessage(err)) || "Không mở được bài giảng.");
  } finally {
    setBusy(null);
  }
}

export function pickBaiGiangDoc(docs) {
  const arr = Array.isArray(docs) ? docs : [];
  return arr.find((d) => isBaiGiangType(d.type)) || null;
}

/** Tài liệu không phải bài giảng ZIP (giáo án, khác…) */
export function pickOtherDoc(docs) {
  const arr = Array.isArray(docs) ? docs : [];
  return arr.find((d) => !isBaiGiangType(d.type)) || null;
}

export function pickFirstPdfDoc(docs) {
  const arr = Array.isArray(docs) ? docs : [];
  return arr.find((d) => isPdfDoc(d)) || null;
}

export function formatDocSize(bytes) {
  const n = Number(bytes);
  if (!Number.isFinite(n) || n < 0) return "—";
  if (n >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(2)} GB`;
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)} MB`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(2)} KB`;
  return `${n} B`;
}

export { MAX_ZIP_BYTES_FOR_INLINE_PLAYER };
